import React, { useState, useEffect } from "react";
import {
  Table,
  Space,
  Input,
  Tooltip,
  Button,
  Segmented,
  DatePicker,
  Popconfirm,
  Upload,
  Dropdown,
} from "antd";
import { useDispatch } from "react-redux";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import { TrainingAPI } from "../../api/TrainingAPI";
import dayjs from "dayjs";
import { DownOutlined } from "@ant-design/icons";
import "@styles/styles-Settings.css";
import { 
  PermissionedButton, 
  PermissionedInput, 
  PermissionedDatePicker, 
  PermissionedPopConfirm, 
  usePermissionCheck
} from "../PermissionedComponents";

export const GameTable = () => {
  const [opponent, setOpponent] = useState(undefined);
  const [date, setDate] = useState(null);
  const [home, setHome] = useState(true);
  const [data, setData] = useState([]);
  const trainingAPI = new TrainingAPI();
  const dispatch = useDispatch();
  const { permissionDisabled } = usePermissionCheck("AddGames");
  const date_format = "YYYY-MM-DD";

  const handleDelete = (game_id) => {
    trainingAPI
      .deleteGame(game_id)
      .then((data) => {
        message.success("Game deleted");
        getGameData();
      })
      .catch((data) => message.error("Error deleting game"));
  };

  const onDateChange = (date, dateString) => {
    setDate(dateString === "" ? null : dateString);
  };

  const onHomeChange = (value) => {
    setHome(value === "Home");
  };

  const onOpponentChange = (e) => {
    setOpponent(e.target.value);
  };

  const getGameData = () => {
    trainingAPI.getAllGames().then((data) => {
      setData(data);
    });
  };

  const onSubmit = (bulk = false, file = null) => {
    trainingAPI
      .addGame(opponent, date, home, bulk, file)
      .then((data) => {
        message.success(file ? "Games added" : "Game added");
        getGameData();
      })
      .catch((error) => {
        const fallback = file ? "Error adding games" : "Error adding game";
        let detailText = "";
        try {
          const detail =
            error?.response?.data?.error || error?.response?.data || error?.message;
          detailText =
            typeof detail === "string" ? detail : detail ? JSON.stringify(detail.errors[0]) : "";
        } catch {
          detailText = "";
        }
        message.error(detailText ? `${fallback}: ${detailText}` : fallback);
      });
  };

  const uploadProps = {
    customRequest: async ({ file, onSuccess, onError }) => {
      const bulk = true;
      onSubmit(bulk, file)
    },
    accept: "text/csv",
    showUploadList: false,
  };

  const downloadCSV = () => {
    const csvHeaders = "Opponent,Date,Home";
    const blob = new Blob([csvHeaders], { type: 'text/csv;charset=utf-8;' });

    // Create a URL for the blob object
    const url = window.URL.createObjectURL(blob);
  
    // Create an <a> element and set its href to the blob URL
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Game Schedule Template.csv';
  
    // Append the <a> element to the document body
    document.body.appendChild(a);
  
    // Programmatically click the <a> element to trigger the download
    a.click();
  
    // Clean up by revoking the object URL and removing the <a> element
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const columns = [
    {
      title: "Opponent",
      dataIndex: "opponent",
      key: "opponent",
      sorter: {
        compare: (a, b) => a.opponent.localeCompare(b.opponent),
        multiple: 1,
      },
      render: (opponent, data) => {
        if (data["home"] === true) {
          return <>vs. {opponent}</>;
        } else {
          return <>@ {opponent}</>;
        }
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: {
        compare: (a, b) => a.date.localeCompare(b.date),
        multiple: 3,
      },
      render: (date) => {
        return <>{date}</>;
      },
    },
    {
      title: "",
      dataIndex: "operation",
      width: "10%",
      render: (_, key) => (
        <PermissionedPopConfirm
          permissionCheck="DeleteGames"
          title="Sure to delete?"
          onConfirm={() => handleDelete(key["game_id"])}
        >
          <a style={{ color: "#1890ff" }}>Delete</a>
        </PermissionedPopConfirm>
      ),
    },
  ];

  useEffect(() => {
    getGameData();
  }, []);

  return (
    <>
      <div className="settings-row">
      <Space wrap>
        <div className="injury-item">
          <PermissionedInput
            permissionCheck="AddGames"
            style={{ width: 200 }}
            placeholder="Opponent Name"
            value={opponent}
            onChange={onOpponentChange}
          />
        </div>
        <div className="injury-item">
          <PermissionedDatePicker
            permissionCheck="AddGames"
            onChange={onDateChange}
            value={date !== null ? dayjs(date, date_format) : null}
          />
        </div>
        <div className="injury-item">
          <Segmented
            options={["Home", "Away"]}
            value={home ? "Home" : "Away"}
            onChange={onHomeChange}
            disabled={permissionDisabled}
          />
        </div>
        {opponent === undefined || date === null ? (
          <Tooltip title="Enter an opponent and a date">
            <PermissionedButton type="primary" disabled permissionCheck="AddGames">
              Add Game
            </PermissionedButton>
          </Tooltip>
        ) : (
          <PermissionedButton type="primary" onClick={() => onSubmit()} permissionCheck="AddGames">
            Add Game
          </PermissionedButton>
        )}
        <Space.Compact>
          <Upload {...uploadProps}>
            <PermissionedButton permissionCheck="AddGames">Bulk add games</PermissionedButton>
          </Upload>
          <Dropdown
            trigger={["click"]}
            menu={{ items: [{ key: "template", label: "Download CSV template", onClick: downloadCSV }] }}
          >
            <PermissionedButton permissionCheck="AddGames" icon={<DownOutlined />} aria-label="More CSV options" />
          </Dropdown>
        </Space.Compact>
      </Space>
      </div>
      <div className="report-injury-header2">Scheduled Games</div>
      <div className="settings-row">
        <Table
          rowKey="rowId"
          columns={columns}
          size="small"
          dataSource={data}
          style={{ width: 440, maxWidth: "100%" }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "30"],
            position: ["bottomRight"],
          }}
        />
      </div>
    </>
  );
};
